/* W72 STEP 8 — WHAT CHANGED: the ONE reader of `CHANGELOG.md` (repo root).
 *
 * Two surfaces show the list — the public page `coachwork.io/changes` (`web/changes.html`, filled at build
 * by the `ht-changelog` plugin in `vite.config.ts`) and the Account's `What changed` tab — and both read
 * the file through THIS module, so there is one list and never two. `tools/testflight/promote.sh` asks the
 * same parser (through `node`, which strips the types) whether a build has its entry before promoting it.
 *
 * ⚠️ THE FILE IS STRICT, AND A MALFORMED ENTRY FAILS LOUDLY WITH ITS LINE NUMBER. A lenient reader would
 * drop the line it could not read and publish the rest, which is the exact way a changelog stops being
 * true without anybody noticing.
 *
 * ⚠️ WHO ASKED IS A GRAMMAR, NOT A FREE FIELD (his 2026-09-08 line: real people, first name, consent).
 * A reporter is a VERB ("Asked by", "Reported by", "Found by", "From", "Direction from") and a SUBJECT
 * that is either a first name listed on the file's `<!-- consented: … -->` line, or one of the fixed
 * generic subjects below. An unconsented name cannot be written into the file without the parser
 * refusing it.
 *
 * ⚠️ ERASABLE TYPESCRIPT ONLY (no enums, no parameter properties): `node` imports this file directly by
 * stripping the types, which is how the promotion script and the build plugin share it.
 */

export const CHANGE_KINDS = ["Added", "Changed", "Deprecated", "Removed", "Fixed", "Security"] as const;
export type ChangeKind = (typeof CHANGE_KINDS)[number];

export const REPORTER_VERBS = ["Asked by", "Reported by", "Found by", "Direction from", "From"] as const;
export const GENERIC_SUBJECTS = [
  "a coach in the beta", "coaches in the beta", "an athlete in the beta", "our own checks", "our own roadmap",
  "coaches of every rival app in their reviews",
] as const;

export type ChangeEntry = { kind: ChangeKind; title: string; sentence: string; reporter: string; line: number };
export type Release = {
  /** `build 90`, `web`, or `Unreleased`. */
  label: string;
  build: number | null;
  /** YYYY-MM-DD; null only for Unreleased. */
  date: string | null;
  entries: ChangeEntry[];
  line: number;
};
export type Changelog = { consented: string[]; releases: Release[] };

export class ChangelogError extends Error {
  lineNumber: number;
  constructor(lineNumber: number, message: string) {
    super(`CHANGELOG.md line ${lineNumber}: ${message}`);
    this.lineNumber = lineNumber;
    this.name = "ChangelogError";
  }
}

const RELEASE = /^## \[(Unreleased|build (\d+)|web)\](?: - (\d{4}-\d{2}-\d{2}))?\s*$/;
const KIND = /^### (\S+)\s*$/;
const ENTRY = /^- \*\*(.+?)\*\* — (.+) — (.+)$/;
const CONSENT = /^<!-- consented:(.*)-->\s*$/;

function validDate(s: string): boolean {
  const t = Date.parse(`${s}T12:00:00Z`);
  return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === s;
}

/** One sentence: it ends with a full stop (or ! / ?), and no other sentence ends inside it. */
function isOneSentence(s: string): boolean {
  if (!/[.!?]$/.test(s)) return false;
  return !/[.!?]\s+[A-Z]/.test(s.slice(0, -1));
}

export function reporterFault(reporter: string, consented: readonly string[]): string | null {
  const verb = REPORTER_VERBS.find((v) => reporter.startsWith(`${v} `));
  if (verb === undefined) return `the reporter must start with one of: ${REPORTER_VERBS.join(", ")}`;
  const subject = reporter.slice(verb.length + 1);
  if ((GENERIC_SUBJECTS as readonly string[]).includes(subject)) return null;
  const name = subject.split(/[\s,']/)[0] ?? "";
  if (consented.includes(name)) return null;
  return `"${subject}" names nobody who consented — use a name on the consented line, or one of: ${GENERIC_SUBJECTS.join(" · ")}`;
}

export function parseChangelog(text: string): Changelog {
  const lines = text.split(/\r?\n/);
  let consented: string[] | null = null;
  const releases: Release[] = [];
  let release: Release | null = null;
  let kind: ChangeKind | null = null;
  let inComment = false;
  lines.forEach((raw, i) => {
    const n = i + 1;
    const line = raw.trimEnd();
    const consent = CONSENT.exec(line);
    if (consent) {
      if (release !== null) throw new ChangelogError(n, "the consented line belongs above the first release");
      consented = consent[1]!.split(",").map((s) => s.trim()).filter((s) => s !== "");
      return;
    }
    if (inComment) { if (line.includes("-->")) inComment = false; return; }
    if (line.startsWith("<!--")) { if (!line.includes("-->")) inComment = true; return; }
    const r = RELEASE.exec(line);
    if (r) {
      const label = r[1]!;
      const date = r[3] ?? null;
      if (label === "Unreleased" && date !== null) throw new ChangelogError(n, "Unreleased carries no date");
      if (label !== "Unreleased" && date === null) throw new ChangelogError(n, `[${label}] needs a date: "## [${label}] - YYYY-MM-DD"`);
      if (date !== null && !validDate(date)) throw new ChangelogError(n, `"${date}" is not a real date`);
      if (release !== null && release.entries.length === 0) throw new ChangelogError(release.line, `[${release.label}] has no entries`);
      release = { label, build: r[2] ? Number(r[2]) : null, date, entries: [], line: n };
      releases.push(release);
      kind = null;
      return;
    }
    if (line.startsWith("## ")) throw new ChangelogError(n, `a release heading reads "## [build N] - YYYY-MM-DD", "## [web] - YYYY-MM-DD" or "## [Unreleased]" — got "${line}"`);
    if (release === null) return; // the preamble above the first release is prose
    const k = KIND.exec(line);
    if (k) {
      if (!(CHANGE_KINDS as readonly string[]).includes(k[1]!)) throw new ChangelogError(n, `"${k[1]}" is not one of ${CHANGE_KINDS.join(" · ")}`);
      kind = k[1] as ChangeKind;
      return;
    }
    if (line.trim() === "") return;
    const e = ENTRY.exec(line);
    if (!e) throw new ChangelogError(n, `an entry reads "- **Title** — One sentence. — Asked by …" — got "${line}"`);
    if (kind === null) throw new ChangelogError(n, "an entry needs a ### Added/Changed/… heading above it");
    const [, title, sentence, reporter] = e as unknown as [string, string, string, string];
    if (!isOneSentence(sentence.trim())) throw new ChangelogError(n, `the entry must be ONE sentence ending in a full stop — got "${sentence}"`);
    const fault = reporterFault(reporter.trim(), consented ?? []);
    if (fault !== null) throw new ChangelogError(n, fault);
    release.entries.push({ kind, title: title.trim(), sentence: sentence.trim(), reporter: reporter.trim(), line: n });
  });
  const last = releases[releases.length - 1];
  if (last !== undefined && last.entries.length === 0) throw new ChangelogError(last.line, `[${last.label}] has no entries`);
  if (consented === null) throw new ChangelogError(1, `the file needs a "<!-- consented: … -->" line (it may be empty)`);
  // Newest first, Unreleased only at the top, a build number once.
  const seen = new Set<number>();
  releases.forEach((r, i) => {
    if (r.label === "Unreleased" && i !== 0) throw new ChangelogError(r.line, "Unreleased goes first");
    if (r.build !== null) {
      if (seen.has(r.build)) throw new ChangelogError(r.line, `build ${r.build} appears twice`);
      seen.add(r.build);
    }
    const prev = releases.slice(0, i).reverse().find((p) => p.date !== null);
    if (prev && r.date !== null && prev.date! < r.date) throw new ChangelogError(r.line, `releases run newest first — ${r.date} is after ${prev.date}`);
  });
  return { consented: consented as string[], releases };
}

/** Does the file carry an entry for this build? The promotion gate's whole question. */
export function hasBuild(log: Changelog, build: number): boolean {
  return log.releases.some((r) => r.build === build && r.entries.length > 0);
}

// ── Grouping for the two surfaces: by week (Monday first, the phone's `ProgramWeekCalendar` rule) ──

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function mondayOf(date: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  const back = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - back);
  return d.toISOString().slice(0, 10);
}

export function dayLabel(date: string): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

export type WeekGroup = { key: string; heading: string; entries: (ChangeEntry & { label: string })[] };

/** One group per week, newest first; its heading names the week and every build in it. */
export function weekGroups(log: Changelog): WeekGroup[] {
  const groups: WeekGroup[] = [];
  for (const r of log.releases) {
    const key = r.date === null ? "next" : mondayOf(r.date);
    let g = groups.find((x) => x.key === key);
    if (!g) { g = { key, heading: "", entries: [] }; groups.push(g); }
    for (const e of r.entries) g.entries.push({ ...e, label: r.label });
  }
  for (const g of groups) {
    if (g.key === "next") { g.heading = "Coming next"; continue; }
    const builds = [...new Set(log.releases.filter((r) => r.date !== null && mondayOf(r.date) === g.key && r.build !== null)
      .map((r) => `build ${r.build}`))];
    g.heading = [`Week of ${dayLabel(g.key)}`, ...builds].join(" · ");
  }
  return groups;
}

// ── The public page's HTML, rendered at build time (no script ships with it) ──

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

export function renderChangesHtml(log: Changelog): string {
  return weekGroups(log).map((g) => [
    `<section class="changes-week">`,
    `<h2 class="changes-week-head">${esc(g.heading)}</h2>`,
    `<ol class="changes-list">`,
    ...g.entries.map((e) => [
      `<li class="changes-entry">`,
      `<span class="changes-kind changes-kind-${e.kind.toLowerCase()}">${esc(e.kind)}</span>`,
      `<div class="changes-body"><h3 class="changes-title">${esc(e.title)}</h3>`,
      `<p class="changes-what">${esc(e.sentence)} <span class="changes-who">${esc(e.reporter)}.</span></p></div>`,
      `</li>`,
    ].join("")),
    `</ol>`,
    `</section>`,
  ].join("\n")).join("\n");
}
